<template>
  <div class="p-6 space-y-6">
    <LoadingIndicator v-if="loading" />

    <div v-else-if="error" class="bg-red-100 text-red-800 rounded p-4 text-sm">
      {{ error }}
    </div>

    <div v-else-if="!run" class="bg-gray-25 border border-gray-50 rounded p-8 text-center">
      <p class="text-gray-800 font-medium">No completed Hades run yet.</p>
      <p class="text-gray-600 text-sm mt-2">
        The nightly duty investigates every open requirement and derives its queue.
        Results appear here after the first run completes.
      </p>
    </div>

    <template v-else>
      <div class="flex items-baseline justify-between">
        <div class="text-sm text-gray-600">
          Run {{ run.id }} · {{ run.model }} ·
          {{ run.requirements_selected }} investigated,
          {{ run.requirements_skipped }} unchanged ·
          {{ formatDate(run.completed_at) }}
        </div>
        <button type="button" class="text-primary-700 text-sm hover:underline" @click="fetchPortfolio">
          Refresh
        </button>
      </div>

      <!-- The headline. Engineering work with no traceable customer request is
           the finding, not a footnote — so it leads. -->
      <div class="bg-white border border-gray-50 rounded p-4">
        <div class="flex items-center gap-8">
          <div>
            <div class="text-3xl font-semibold text-gray-900">
              {{ unlinkedCount }}<span class="text-gray-400 text-xl"> / {{ prTotal }}</span>
            </div>
            <div class="text-sm text-gray-600 mt-1">
              pull requests with no traceable customer request
            </div>
          </div>
          <div class="text-sm text-gray-600 max-w-xl">
            Some of this is legitimate platform work that no customer would think
            to ask for. Some is not. The Engineering section below splits it by
            category.
          </div>
        </div>
      </div>

      <div>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-base font-semibold text-gray-900">Tickets</h3>
          <div class="text-sm text-gray-600">{{ filteredTickets.length }} shown</div>
        </div>
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            v-for="option in queueFilters"
            :key="option.value"
            type="button"
            class="px-3 py-1 rounded text-xs border"
            :class="queueFilter === option.value
              ? 'bg-primary-100 border-primary-500 text-gray-900'
              : 'bg-white border-gray-50 text-gray-600 hover:border-gray-200'"
            @click="queueFilter = option.value"
          >
            {{ option.label }}<span class="ml-1 text-gray-400">{{ option.count }}</span>
          </button>
        </div>
        <Hades_TicketTable :tickets="filteredTickets" @closed="fetchPortfolio" />
      </div>

      <div>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-base font-semibold text-gray-900">Engineering</h3>
          <div class="text-sm text-gray-600">{{ filteredPrs.length }} shown</div>
        </div>
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            v-for="option in categoryFilters"
            :key="option.value"
            type="button"
            class="px-3 py-1 rounded text-xs border"
            :class="categoryFilter === option.value
              ? 'bg-primary-100 border-primary-500 text-gray-900'
              : 'bg-white border-gray-50 text-gray-600 hover:border-gray-200'"
            @click="categoryFilter = option.value"
          >
            {{ option.label }}<span class="ml-1 text-gray-400">{{ option.count }}</span>
          </button>
        </div>
        <Hades_PRTable :pull-requests="filteredPrs" />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Hades_TicketTable from './Hades_TicketTable.vue';
import Hades_PRTable from './Hades_PRTable.vue';
import { QUEUE_LABELS, CATEGORY_LABELS } from './hades_constants';

export default {
  name: 'Support_Hades',
  components: { LoadingIndicator, Hades_TicketTable, Hades_PRTable },
  data() {
    return {
      loading: true,
      error: null,
      run: null,
      tickets: [],
      pullRequests: [],
      queueCounts: {},
      categoryCounts: {},
      unlinkedCount: 0,
      prTotal: 0,
      queueFilter: 'all',
      categoryFilter: 'all',
    };
  },
  computed: {
    queueFilters() {
      const options = [{ value: 'all', label: 'All', count: this.tickets.length }];
      Object.entries(this.queueCounts).forEach(([queue, count]) => {
        options.push({ value: queue, label: QUEUE_LABELS[queue] || queue, count });
      });
      return options;
    },
    categoryFilters() {
      const options = [{ value: 'all', label: 'All', count: this.pullRequests.length }];
      Object.entries(this.categoryCounts).forEach(([category, count]) => {
        options.push({
          value: category,
          label: CATEGORY_LABELS[category] || category,
          count,
        });
      });
      return options;
    },
    filteredTickets() {
      if (this.queueFilter === 'all') return this.tickets;
      return this.tickets.filter((t) => (t.queues || []).includes(this.queueFilter));
    },
    filteredPrs() {
      if (this.categoryFilter === 'all') return this.pullRequests;
      return this.pullRequests.filter((p) => p.primary_category === this.categoryFilter);
    },
  },
  created() {
    this.fetchPortfolio();
  },
  methods: {
    async fetchPortfolio() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/hades/portfolio/');
        this.run = data.run;
        this.tickets = data.tickets || [];
        this.pullRequests = data.pull_requests || [];
        this.queueCounts = data.queue_counts || {};
        this.categoryCounts = data.category_counts || {};
        this.unlinkedCount = data.pull_requests_unlinked || 0;
        this.prTotal = data.pull_requests_total || 0;
      } catch (e) {
        this.error = e.response?.data?.detail || 'Could not load the Hades portfolio.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    },
  },
};
</script>
